import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  duration: number; // ms
  createdAt: number;
}

let nextId = 1;

export const useNotificationStore = defineStore('notifications', () => {
  // ─── State ───
  const notifications = ref<Notification[]>([]);

  // ─── Getters ───
  const allNotifications = computed(() => notifications.value);

  // ─── Actions ───

  function add(type: NotificationType, title: string, message: string, duration = 5000) {
    const id = nextId++;
    const notification: Notification = {
      id,
      type,
      title,
      message,
      duration,
      createdAt: Date.now(),
    };

    notifications.value.unshift(notification);

    // Auto-remove después de la duración
    setTimeout(() => {
      remove(id);
    }, duration + 300); // +300ms por la animación de salida

    return id;
  }

  function remove(id: number) {
    const idx = notifications.value.findIndex((n) => n.id === id);
    if (idx > -1) {
      notifications.value.splice(idx, 1);
    }
  }

  function clearAll() {
    notifications.value = [];
  }

  // Shorthand helpers
  function success(title: string, message: string, duration?: number) {
    return add('success', title, message, duration);
  }

  function error(title: string, message: string, duration?: number) {
    return add('error', title, message, duration);
  }

  function warning(title: string, message: string, duration?: number) {
    return add('warning', title, message, duration);
  }

  function info(title: string, message: string, duration?: number) {
    return add('info', title, message, duration);
  }

  return {
    notifications,
    allNotifications,
    add,
    remove,
    clearAll,
    success,
    error,
    warning,
    info,
  };
});
